<!-- eslint-disable @typescript-eslint/no-explicit-any -->
<script setup lang="ts">
  import { onMounted, ref, watch } from 'vue'
  import type { FieldConfig, FormData } from '@/types'
  import { getNestedValue, getRangeValues, updateSliderFill } from '@/utils/formHelper'
  import SelectInput from './select-input.vue'

  const props = defineProps<{
    field: FieldConfig,
    formData: FormData,
    sliderValue: Record<string, number>,
    nextSelectField: FieldConfig | null,
    index: number,
  }>()

  const emit = defineEmits<{
    (e: 'update-field', key: string, value: FormData[keyof FormData]): void,
  }>()

  const sliderRef = ref<HTMLInputElement | null>(null)

  const onInput = (e: Event) => {
    const slider = e.target as HTMLInputElement
    const idx = Math.round(slider.valueAsNumber)
    slider.value = String(idx)
    updateSliderFill(slider)
    const { values } = getRangeValues(props.field, props.formData)
    emit('update-field', props.field.key, values[idx] as any)
  }

  const handleLabelClick = (idx: number) => {
    const { values } = getRangeValues(props.field, props.formData)
    emit('update-field', props.field.key, values[idx] as any)
    if (sliderRef.value) {
      sliderRef.value.value = String(idx)
      updateSliderFill(sliderRef.value)
    }
  }

  const resetSlider = () => {
    const slider = sliderRef.value
    if (slider) {
      slider.value = '0'
      updateSliderFill(slider)
      const { values } = getRangeValues(props.field, props.formData)
      emit('update-field', props.field.key, values[0] as any)
    }
  }

  onMounted(() => {
    const slider = sliderRef.value
    if (slider) {
      const idx = props.sliderValue[props.field.key] ?? 0
      slider.value = String(idx)
      updateSliderFill(slider)
    }
  })

  // Watch for changes in typeVal and freqVal
  watch(
    () => {
      if (props.field.condition && props.field.key.includes('.count')) {
        const typeVal = getNestedValue(props.formData, props.field.condition.key)
        const freqKey = props.field.condition.key.replace(/\.type$/, '.frequency')
        const freqVal = getNestedValue(props.formData, freqKey)
        return [typeVal, freqVal]
      }
      return []
    },
    () => {
      // Reset slider when typeVal or freqVal changes
      if (props.field.key.includes('.count')) {
        resetSlider()
      }
    },
    { deep: true },
  )
</script>

<template>
  <div
    v-if="
      !props.field.condition ||
        props.field.condition.values.includes(
          getNestedValue(props.formData, props.field.condition.key) as string,
        )
    ">
    <div
      :class="[
        'grid gap-6 items-center',
        nextSelectField ? 'grid-cols-1 sm:grid-cols-2' : 'grid-cols-1',
      ]">
      <div class="w-full">
        <div class="flex justify-between mb-2">
          <span
            v-for="(label, idx) in getRangeValues(props.field, props.formData).labels"
            :key="idx"
            class="text-sm text-secondary-2 cursor-pointer select-none"
            :class="{
              'font-medium text-secondary-3': sliderValue[field.key] === idx,
            }"
            @click="handleLabelClick(idx)">{{ label }}</span>
        </div>
        <input
          ref="sliderRef"
          :value="props.sliderValue[field.key]"
          type="range"
          :min="0"
          :max="getRangeValues(props.field, props.formData).values.length - 1"
          step="1"
          class="peer appearance-none w-full h-1 bg-gray-200 rounded-lg focus-visible:ring-2 focus-visible:ring-offset-2 transition-all focus-visible:ring-primary-700/20"
          @input="onInput">
      </div>
      <select-input
        v-if="props.nextSelectField"
        :field="props.nextSelectField"
        :form-data="props.formData"
        @update-field="(k, v) => emit('update-field', k, v)" />
    </div>
  </div>
</template>

<style scoped>
@reference "@/assets/styles/main.css";

input[type="range"] {
  appearance: none;
  width: 100%;
  height: 4px;
  background: var(--color-gray-200);
  border-radius: 2px;
  outline: none;
  --fill-percent: 0%;

  background-image:
    linear-gradient(
      to right,
      var(--color-primary-700) var(--fill-percent),
      transparent var(--fill-percent)
    );
}

input[type="range"]::-webkit-slider-runnable-track {
  height: 4px;
  background: transparent;
}

input[type="range"]::-webkit-slider-thumb {
  appearance: none;
  width: 19px;
  height: 19px;
  background: white;
  border: 5px solid var(--color-primary-700);
  border-radius: 50%;
  cursor: pointer;
  margin-top: -8.5px;
  box-shadow: 0 1px 3px rgb(0 0 0 / 10%);
}

input[type="range"]::-moz-range-track {
  height: 4px;
  background: var(--color-gray-200);
  border-radius: 2px;
}

input[type="range"]::-moz-range-thumb {
  width: 16px;
  height: 16px;
  background: white;
  border: 4px solid var(--color-primary-700);
  border-radius: 50%;
  cursor: pointer;
  box-shadow: 0 1px 3px rgb(0 0 0 / 10%);
}

input[type="range"]::-moz-range-progress {
  background: var(--color-primary-700);
  height: 4px;
  border-radius: 2px;
}
</style>
